import type { ApolloClient } from "@apollo/client";
import { fetchCatalogue, openGame } from "./api";
import { roleplayErrorCode } from "./contract";
import { gameEntry, type UnavailableReason } from "./entry";
import { restartRoleplaySession, startRoleplaySession } from "./genesis";

export type NewGame = { kind: "game"; gameId: string } | { kind: "unavailable"; reason: UnavailableReason };

/** Replace the player's unfinished plane game before entering the new story. */
export async function replacePlaneGame(client: ApolloClient): Promise<string> {
  const gameId = (await restartRoleplaySession(client)).session.id;
  await openGame(gameId);
  return gameId;
}

/** Open Genesis's current plane game, resolving a replacement after expiry or a concurrent restart. */
export async function startPlaneGame(client: ApolloClient): Promise<NewGame> {
  const started = (await startRoleplaySession(client)).session.id;
  try {
    await openGame(started);
    return { kind: "game", gameId: started };
  } catch (error) {
    const code = roleplayErrorCode(error);
    if (code !== "ROLEPLAY_GAME_EXPIRED" && code !== "ROLEPLAY_GAME_CLOSED") return { kind: "game", gameId: started };
    const entry = gameEntry(await fetchCatalogue());
    if (entry.kind === "continue" && entry.gameId.toLowerCase() !== started.toLowerCase()) return { kind: "game", gameId: entry.gameId };
    if (entry.kind === "unavailable") return entry;
    if (code === "ROLEPLAY_GAME_CLOSED") {
      const current = (await startRoleplaySession(client)).session.id;
      if (current.toLowerCase() === started.toLowerCase()) throw error;
      await openGame(current);
      return { kind: "game", gameId: current };
    }
    return { kind: "game", gameId: (await restartRoleplaySession(client)).session.id };
  }
}
